package br.com.htms.inspeccaocampo.data.apr

import br.com.htms.inspeccaocampo.data.epi.EpiCatalog
import br.com.htms.inspeccaocampo.data.epi.EpiUso
import br.com.htms.inspeccaocampo.data.funcionarios.Funcionario

/** Linha da tabela "Descrição da técnica / definição de responsabilidades". */
data class AprEtapa(
    val itemPex: String = "",
    val etapas: String = "",
    val perigos: String = "",
    val pac: String = "",
    val medidas: String = "",
    val oQuePodeDarErrado: String = "",
    val medidasAdicionais: String = ""
)

data class AprPessoa(val nome: String = "", val matricula: String = "")

data class AprExecutante(val nome: String = "", val empresa: String = AprCatalog.EMPRESA_PADRAO)

enum class AprSecao(val titulo: String, val declaracao: String) {
    ELABORACAO(
        "RESPONSÁVEIS PELA ELABORAÇÃO DA APR",
        "Declaro que as informações prestadas nesse documento foram niveladas com todos os membros da equipe de " +
            "planejamento, e estão aptas a serem avaliadas no Fórum de Aprovação."
    ),
    FORUM(
        "REGISTRO DO FÓRUM DE APROVAÇÃO",
        "Declaro que as informações prestadas nesse documento foram devidamente analisadas e discutidas pelo Comitê " +
            "de Análise Crítica, e atendem às exigências operacionais e de Saúde e Segurança do Trabalho da Axia."
    ),
    RPAC(
        "REGISTRO DA RPAC",
        "Declaro que as informações prestadas nesse documento foram completamente discutidas e niveladas com todos " +
            "os membros da equipe executiva, antes do início da intervenção."
    ),
    REVALIDACAO(
        "REVALIDAÇÃO DA APR (PARA ATIVIDADES PREVIAMENTE APROVADAS EM FÓRUM ANTERIOR, COM MESMO ESCOPO E COM PRAZO " +
            "ACIMA DE 15 DIAS DE EXECUÇÃO)",
        "Declaro que os perigos e riscos e medidas de controle analisados e aprovados premanecem os mesmos após 15 " +
            "dias de execução das atividades, e que as informações prestadas nesse documento foram reforçadas com " +
            "todos os membros da equipe executiva."
    )
}

/** Blocos de assinatura do modelo; [linhaModelo] é a linha de Nome/Matrícula na planilha modelo. */
enum class AprAssinatura(val secao: AprSecao, val rotulo: String, val rotuloData: String, val linhaModelo: Int) {
    ELAB_RESPONSAVEL(AprSecao.ELABORACAO, AprCatalog.RESP_INTERVENCAO, "Data da Aprovação", 28),
    ELAB_SST(AprSecao.ELABORACAO, "ENGENHEIRO OU TÉCNICO DE SST", "Data da Aprovação", 31),
    FORUM_OEM(AprSecao.FORUM, AprCatalog.COORD_OEM, "Data da Aprovação", 37),
    FORUM_SST(AprSecao.FORUM, AprCatalog.COORD_SST, "Data da Aprovação", 40),
    FORUM_RESPONSAVEL(AprSecao.FORUM, AprCatalog.RESP_INTERVENCAO, "Data da Aprovação", 43),
    RPAC_RESPONSAVEL(AprSecao.RPAC, AprCatalog.RESP_INTERVENCAO, "Data da RPAC", 49),
    REVAL_OEM(AprSecao.REVALIDACAO, AprCatalog.COORD_OEM, "Data da Revalidação", 55),
    REVAL_SST(AprSecao.REVALIDACAO, AprCatalog.COORD_SST, "Data da Revalidação", 58),
    REVAL_RESPONSAVEL(AprSecao.REVALIDACAO, AprCatalog.RESP_INTERVENCAO, "Data da Revalidação", 61)
}

data class AprDados(
    val numeroApr: String = "1",
    val numeroSiOs: String = "",
    val atividade: String = "",
    val instalacao: String = "",
    val local: String = "",
    val emissao: String = "",
    val hora: String = "07:00",
    val vigencia: String = "",
    val responsavel: String = "",
    val substitutos: String = "",
    val maquinas: String = "",
    val episObrigatorios: String = "",
    val episEventuais: String = "",
    val epcsObrigatorios: String = "",
    val epcsEventuais: String = "",
    val etapas: List<AprEtapa> = listOf(AprEtapa()),
    val observacoes: String = "",
    val assinaturas: Map<AprAssinatura, AprPessoa> = emptyMap(),
    val executantes: List<AprExecutante> = emptyList()
) {
    fun pessoa(a: AprAssinatura): AprPessoa = assinaturas[a] ?: AprPessoa()

    /** Hora como no modelo ("07:00h"). */
    val horaFormatada: String
        get() = hora.trim().let { if (Regex("^\\d{1,2}:\\d{2}$").matches(it)) "${it}h" else it }

    /** Ano exibido no cabeçalho ("HTMS ENERGIA 2026"). */
    val ano: String get() = emissao.trim().substringAfterLast('/', "").takeIf { it.length == 4 } ?: ""
}

object AprCatalog {
    const val EMPRESA_PADRAO = "HTMS ENERGIA"
    const val RESP_INTERVENCAO = "RESPONSÁVEL PELA INTERVENÇÃO OU ATIVIDADE - COO/VEP/VGS"
    const val COORD_OEM = "COORDENADOR OU DESIGNADO DE O&M - COO/VEP/VGS"
    const val COORD_SST = "COORDENADOR OU DESIGNADO DE SST - COO/VEP/VGS"
    const val TEXTO_EXECUTANTES =
        "Este formulário atende a O.S. (Ordem de Serviço) estabelecida na Norma Regulamentadora nº 1 (NR 01) do " +
            "ministério do Trabalho e Emprego (Lei 6.514 / Portaria 3.214/78). Após ter avaliado os riscos das " +
            "atividades, em conjunto com o emitente e liberador, declaro estar ciente de minha responsabilidade na " +
            "execução dessa tarefa, devendo seguir as medidas preventivas definidas neste documento."

    /** EPIs da Matriz de EPI e EPC (mesmas colunas da matriz por função). */
    val epis: List<String> get() = EpiCatalog.colunas.map { it.nome.uppercase() }

    /** Aba "EPI E EPC" da Matriz: Equipamentos de Proteção Coletiva. */
    val epcs: List<String> = listOf(
        "CONE",
        "CERQUITE",
        "EXTINTORES",
        "VARA DE MANOBRA",
        "DETECTORES DE TENSÃO",
        "CONJUNTO DE ATERRAMENTO TEMPORÁRIO",
        "FITA ZEBRADA DE ISOLAMENTO",
        "PLACAS DE SINALIZAÇÃO"
    )

    private const val SEP = "; "

    fun itens(texto: String): List<String> = texto.split(';').map { it.trim() }.filter { it.isNotEmpty() }

    fun contem(texto: String, item: String): Boolean = itens(texto).any { it.equals(item, ignoreCase = true) }

    /** Liga/desliga [item] na lista separada por "; ". */
    fun alternar(texto: String, item: String): String {
        val atual = itens(texto)
        return if (atual.any { it.equals(item, ignoreCase = true) }) {
            atual.filterNot { it.equals(item, ignoreCase = true) }.joinToString(SEP)
        } else (atual + item).joinToString(SEP)
    }

    fun adicionar(texto: String, item: String): String =
        if (item.isBlank() || contem(texto, item)) texto else (itens(texto) + item.trim()).joinToString(SEP)

    /** "NOME (CARGO)" como no modelo. */
    fun rotuloPessoa(f: Funcionario): String =
        if (f.cargoNome.isBlank()) f.nome else "${f.nome} (${f.cargoNome.uppercase()})"

    /**
     * EPIs sugeridos pela Matriz para os cargos informados:
     * X = obrigatório; XX/XXX = eventual (quando não for obrigatório para outro cargo).
     */
    fun sugerirEpis(funcionarios: List<Funcionario>): Pair<List<String>, List<String>> {
        val obrig = LinkedHashSet<String>()
        val event = LinkedHashSet<String>()
        funcionarios.forEach { f ->
            val setor = f.setorGhe.substringBeforeLast(" - GHE").trim()
            val linha = EpiCatalog.encontrarFuncao(setor, f.cargoNome) ?: return@forEach
            linha.usos.forEach { (col, uso) ->
                val nome = col.nome.uppercase()
                if (uso == EpiUso.BASICO) obrig += nome else event += nome
            }
        }
        val ordem = epis
        return ordem.filter { it in obrig } to ordem.filter { it in event && it !in obrig }
    }
}
